import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Navigate, useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { userApi } from '../services/api';
import hiveService from '../services/hive';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import RichEditor from '../components/RichEditor';
import MarkdownRenderer from '../components/MarkdownRenderer/MarkdownRenderer';
import BeneficiariesModal from '../components/BeneficiariesModal/BeneficiariesModal';
import Lightbox from '../components/Lightbox/Lightbox';
import { FEED_DISPLAY_CONFIG, getDefaultEnabledFeedKeys } from '../config/feed-details';
import { useLists } from '../context/ListsContext';
import useAuthorAvatar from '../utils/useAuthorAvatar';
import { htmlToMarkdown } from '../components/RichEditor/richEditorUtils';
import { NOTIFICATION_TYPE_LABELS, ALL_NOTIFICATION_TYPES } from '../config/notificationTypes';
import { APP_OPTIONS } from '../config/appOptions';
import './Settings.css';

const VALID_TABS = ['settings', 'notifications', 'profile', 'signature', 'muted'];

const Settings = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { isAuthenticated, user, loginMethod } = useAuth();
  const canUpdateAllHive = loginMethod === 'keychain';
  const navigate = useNavigate();
  const {
    theme,
    accentColor,
    layoutMode,
    maincontentMode,
    communitiesViewMode,
    commentDisplayMode,
    imageDisplayMode,
    showVoteSlider,
    voteDefaultWeight,
    rewardDisplayMode,
    rewardDisplayCurrency,
    singleNotifyOnBlockchain,
    signatureFlux,
    signatureFluxEnabled,
    signatureFlex,
    signatureFlexEnabled,
    signatureComments,
    signatureCommentsEnabled,
    hasUnsavedChanges,
    mutedUsers,
    addMutedUser,
    removeMutedUser,
    updateTheme,
    updateAccentColor,
    updateLayoutMode,
    updateMaincontentMode,
    updateCommunitiesViewMode,
    updateCommentDisplayMode,
    updateImageDisplayMode,
    updateShowVoteSlider,
    updateVoteDefaultWeight,
    updateRewardDisplayMode,
    updateRewardDisplayCurrency,
    updateSingleNotifyOnBlockchain,
    updateSignatureFlux,
    updateSignatureFluxEnabled,
    updateSignatureFlex,
    updateSignatureFlexEnabled,
    updateSignatureComments,
    updateSignatureCommentsEnabled,
    updateDefaultBeneficiaries,
    defaultBeneficiaries,
    startFeed,
    startFeedPath,
    notificationFilterTypes,
    notificationDisplayVoteWeight,
    notiflexionsEnabled,
    notiflexionsGroupByPostAction,
    notiflexionsFromListsEnabled,
    notiflexionsListIds,
    notiflexionsListIncludeComments,
    notiflexionsListIncludeShorts,
    notiflexionsMutedBehaviorEnabled,
    notiflexionsMutedBehaviorMode,
    updateStartFeed,
    updateStartFeedPath,
    updateNotificationFilterTypes,
    updateNotificationDisplayVoteWeight,
    updateNotiflexionsEnabled,
    updateNotiflexionsGroupByPostAction,
    updateNotiflexionsFromListsEnabled,
    updateNotiflexionsListIds,
    updateNotiflexionsListIncludeComments,
    updateNotiflexionsListIncludeShorts,
    updateNotiflexionsMutedBehaviorEnabled,
    updateNotiflexionsMutedBehaviorMode,
    autoload_more,
    updateAutoloadMore,
    saveSettings,
    resetSettings,
    markHasUnsavedChanges
  } = useSettings();
  const { lists } = useLists();
  const listsWithMembers = (lists || []).filter(l => l.members && l.members.length > 0);
  const favouriteListIds = (lists || []).filter((l) => l?.is_favourite).map((l) => l.id);
  const effectiveNotiflexionsListIds = Array.from(new Set([...(notiflexionsListIds || []), ...favouriteListIds]));

  const [savingAll, setSavingAll] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [hasStoredPostingKey, setHasStoredPostingKey] = useState(false);
  const [clearingKey, setClearingKey] = useState(false);
  const [newPostingKey, setNewPostingKey] = useState('');
  const [newPostingKeyStatus, setNewPostingKeyStatus] = useState('idle'); // 'idle'|'verifying'|'valid'|'invalid'
  const [savingKey, setSavingKey] = useState(false);

  const { tab: tabParam } = useParams();
  const normalizedTab = VALID_TABS.includes(tabParam) ? tabParam : 'settings';
  const [activeTab, setActiveTab] = useState(
    normalizedTab
  ); // 'settings', 'notifications', 'profile', or 'muted'

  useEffect(() => {
    if (tabParam && !VALID_TABS.includes(tabParam)) {
      navigate('/settings/settings', { replace: true });
      return;
    }

    setActiveTab(normalizedTab);
  }, [tabParam, normalizedTab, navigate]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    navigate(`/settings/${tab}`, { replace: true });
  };
  const [newMutedUser, setNewMutedUser] = useState('');

  // Profile tab state
  const [profileForm, setProfileForm] = useState({ name: '', about: '', profileImage: '', coverImage: '', website: '', location: '' });
  const [profileAboutHtml, setProfileAboutHtml] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaveStatus, setProfileSaveStatus] = useState(null);
  const [updateMostHive, setUpdateMostHive] = useState(false);
  const [updateAllHive, setUpdateAllHive] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showScopeInfoModal, setShowScopeInfoModal] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [showDefaultBeneficiariesModal, setShowDefaultBeneficiariesModal] = useState(false);
  const [signatureFluxHtml, setSignatureFluxHtml] = useState(signatureFlux || '');
  const [showSignatureFluxPreview, setShowSignatureFluxPreview] = useState(false);
  const [expandedFlux, setExpandedFlux] = useState(false);

  const [signatureFlexHtml, setSignatureFlexHtml] = useState(signatureFlex || '');
  const [showSignatureFlexPreview, setShowSignatureFlexPreview] = useState(false);
  const [expandedFlex, setExpandedFlex] = useState(false);

  const [signatureCommentsHtml, setSignatureCommentsHtml] = useState(signatureComments || '');
  const [showSignatureCommentsPreview, setShowSignatureCommentsPreview] = useState(false);
  const [expandedComments, setExpandedComments] = useState(false);
  const [settingsEnabledFeeds, setSettingsEnabledFeeds] = useState([]);
  const { avatarSources: signaturePreviewAvatarSources, handleAvatarError: handleSignaturePreviewAvatarError } = useAuthorAvatar({
    author: user?.username,
    profileImage: user?.profile?.profileImage,
  });

  // Fetch enabled feeds for start-feed selector
  useEffect(() => {
    const fetchFeeds = async () => {
      try {
        const response = await fetch('/api/feed/feeds-config');
        const feeds = await response.json();
        setSettingsEnabledFeeds(
          feeds
            .filter(f => f.enabled !== false && FEED_DISPLAY_CONFIG[f.key]?.enabledByDefault)
            .map(f => ({ key: f.key, label: FEED_DISPLAY_CONFIG[f.key]?.label || f.label }))
        );
      } catch {
        setSettingsEnabledFeeds(
          getDefaultEnabledFeedKeys().map(key => ({
            key,
            label: FEED_DISPLAY_CONFIG[key]?.label || key,
          }))
        );
      }
    };
    fetchFeeds();
  }, []);

  useEffect(() => {
    setSignatureFluxHtml(signatureFlux || '');
    setSignatureFlexHtml(signatureFlex || '');
    setSignatureCommentsHtml(signatureComments || '');
  }, [signatureFlux, signatureFlex, signatureComments]);

  // Load profile data when profile tab is opened
  useEffect(() => {
    if (activeTab !== 'profile' || !user?.username) return;
    setProfileLoading(true);
    setProfileSaveStatus(null);
    userApi.getProfile(user.username)
      .then(response => {
        const p = response?.user?.profile || {};
        setProfileForm({
          name:         p.name         || '',
          about:        p.about        || '',
          profileImage: p.profileImage || '',
          coverImage:   p.coverImage   || '',
          website:      p.website      || '',
          location:     p.location     || '',
        });
        setProfileAboutHtml(p.about || '');
      })
      .catch(() => {})
      .finally(() => setProfileLoading(false));
  }, [activeTab, user?.username]);

  const handleProfileSave = async () => {
    if (!user?.username) return;
    setProfileSaving(true);
    setProfileSaveStatus(null);
    try {
      await hiveService.updateProfile(user.username, {
        ...profileForm,
        defaultBeneficiaries,
      }, {
        mostHive: updateMostHive,
        allHive: updateAllHive,
      });
      // Always persist defaultBeneficiaries to DB atomically with proflex write.
      // This includes the empty-array case (clearing them) so the two stores stay in sync.
      await fetch(`/api/user/${encodeURIComponent(user.username)}/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ defaultBeneficiaries }),
      });
      // Write a separate proflex_backup custom_json so the data survives other frontends
      // overwriting posting_json_metadata (custom_json ops are never touched by them).
      hiveService.writeProflexBackup(user.username, {
        name:          profileForm.name,
        about:         profileForm.about,
        profile_image: profileForm.profileImage,
        cover_image:   profileForm.coverImage,
        website:       profileForm.website,
        location:      profileForm.location,
        default_beneficiaries: defaultBeneficiaries,
      }).catch(() => {});
      setProfileSaveStatus({ type: 'success', message: 'Profile updated on the blockchain.' });
    } catch (err) {
      setProfileSaveStatus({ type: 'error', message: err.message || 'Failed to update profile.' });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleSignatureFluxChange = (html) => {
    setSignatureFluxHtml(html);
    const markdown = htmlToMarkdown(html);
    updateSignatureFlux(markdown);
    markHasUnsavedChanges(true);
  };

  const handleSignatureFlexChange = (html) => {
    setSignatureFlexHtml(html);
    const markdown = htmlToMarkdown(html);
    updateSignatureFlex(markdown);
    markHasUnsavedChanges(true);
  };

  const handleSignatureCommentsChange = (html) => {
    setSignatureCommentsHtml(html);
    const markdown = htmlToMarkdown(html);
    updateSignatureComments(markdown);
    markHasUnsavedChanges(true);
  };

  // Redirect to home if not logged in
  // Load stored posting key status (only relevant for keychain users)
  // NOTE: must be declared before any early returns to satisfy rules-of-hooks
  useEffect(() => {
    if (!user?.username) return;
    if (hiveService.getLoginMethod() !== 'keychain' && hiveService.getLoginMethod() !== 'postingkey') return;
    userApi.getStoredPostingKeyStatus(user.username)
      .then(({ hasKey }) => setHasStoredPostingKey(hasKey))
      .catch(() => {});
  }, [user?.username]);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />
  }

  const themes = [
    { value: 'light', label: 'Light', description: 'Bright and clean', bgColor: '#ffffff' },
    { value: 'greyed', label: 'Greyed', description: 'Soft and muted', bgColor: '#f5f5f5' },
    { value: 'dimmed', label: 'Dimmed', description: 'Gentle on the eyes', bgColor: '#15202b' },
    { value: 'dark', label: 'Dark', description: 'True dark mode', bgColor: '#000000' },
  ];

  const accentColors = [
    { value: 'blue', label: 'Blue', color: '#1da1f2' },
    { value: 'purple', label: 'Purple', color: '#9c27b0' },
    { value: 'green', label: 'Green', color: '#4caf50' },
    { value: 'orange', label: 'Orange', color: '#ff9800' },
    { value: 'pink', label: 'Pink', color: '#e91e63' },
    { value: 'red', label: 'Red', color: '#c91e3a' },
    { value: 'teal', label: 'Teal', color: '#009688' },
  ];

  const handleSave = async () => {
    setSavingAll(true);
    const success = await saveSettings();
    setSavingAll(false);
    if (success) {
      alert('Settings saved successfully!');
    } else {
      alert('Failed to save settings. Please try again.');
    }
  }; 

  const handleAddMuted = () => {
    const u = newMutedUser.trim().toLowerCase();
    if (u) {
      addMutedUser(u);
      setNewMutedUser('');
    }
  };

  const handleReset = () => {
    if (window.confirm('Discard unsaved changes?')) {
      resetSettings();
    }
  };

  const handleClearStoredPostingKey = async () => {
    if (!user?.username) return;
    if (!window.confirm('Remove your stored posting key? You will need to enter it again when scheduling posts.')) return;
    setClearingKey(true);
    try {
      await userApi.clearStoredPostingKey(user.username);
      setHasStoredPostingKey(false);
    } catch (err) {
      alert('Failed to clear key: ' + err.message);
    } finally {
      setClearingKey(false);
    }
  };

  const handleVerifyNewPostingKey = async () => {
    if (!newPostingKey.trim() || !user?.username) return;
    setNewPostingKeyStatus('verifying');
    try {
      const valid = await hiveService.verifyPostingKey(user.username, newPostingKey.trim());
      setNewPostingKeyStatus(valid ? 'valid' : 'invalid');
    } catch {
      setNewPostingKeyStatus('invalid');
    }
  };

  const handleSaveNewPostingKey = async () => {
    if (!user?.username || newPostingKeyStatus !== 'valid') return;
    setSavingKey(true);
    try {
      await userApi.storePostingKey(user.username, newPostingKey.trim());
      setHasStoredPostingKey(true);
      setNewPostingKey('');
      setNewPostingKeyStatus('idle');
    } catch (err) {
      alert('Failed to save key: ' + err.message);
    } finally {
      setSavingKey(false);
    }
  };

  const handleSyncData = async () => {
    if (!user?.username) {
      alert('User not logged in');
      return;
    }

    setSyncing(true);
    setSyncStatus(null);

    try {
      const response = await fetch('/api/sync/user-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: user.username })
      });

      const result = await response.json();

      if (result.success) {
        setSyncStatus({
          type: 'success',
          message: `Synced successfully! ${result.data.followersSynced} followers (${result.data.newFollowers} new)`
        });
      } else {
        setSyncStatus({
          type: 'warning',
          message: `Sync completed with errors: ${result.data.errors.join(', ')}`
        });
      }
    } catch (error) {
      setSyncStatus({
        type: 'error',
        message: `Sync failed: ${error.message}`
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <>
      <SidebarHeader title="Settings" onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
      <div className="settings-container">
        <div className="settings-header">
          <h1>Settings</h1>
          <p className="settings-subtitle">Customize your experience</p>
        </div>

      <div className="settings-tabs">
        <button
          className={activeTab === 'settings' ? 'tab active' : 'tab'}
          onClick={() => switchTab('settings')}
        >
          Settings
        </button>
        <button
          className={activeTab === 'notifications' ? 'tab active' : 'tab'}
          onClick={() => switchTab('notifications')}
        >
          Notifications
        </button>
        <button
          className={activeTab === 'profile' ? 'tab active' : 'tab'}
          onClick={() => switchTab('profile')}
        >
          Profile
        </button>
        <button
          className={activeTab === 'signature' ? 'tab active' : 'tab'}
          onClick={() => switchTab('signature')}
        >
          Signature
        </button>
        <button
          className={activeTab === 'muted' ? 'tab active' : 'tab'}
          onClick={() => switchTab('muted')}
        >
          Muted users
        </button>
      </div>

      <div className="settings-content">
        {activeTab === 'signature' ? (
          <div className="signature-settings-section" style={{ paddingBottom: '80px' }}>
            <h2 className="section-title" style={{ marginBottom: '1rem' }}>Signature Settings</h2>
            <p className="section-description" style={{ marginBottom: '2rem' }}>
              Configure unique signatures for different types of content.
            </p>

            {/* Comments Signature */}
            <div className="av-section" style={{ marginBottom: '24px' }}>
              <div 
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '16px' }}
                onClick={() => setExpandedComments(!expandedComments)}
              >
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>Use signature on Comments</h3>
                  <p className="option-desc" style={{ margin: '4px 0 0', color: 'var(--text-tertiary)' }}>
                    If active, your signature will be appended to your comments.
                  </p>
                </div>
                <div style={{ paddingLeft: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <input
                    type="checkbox"
                    checked={signatureCommentsEnabled}
                    onChange={(e) => {
                      updateSignatureCommentsEnabled(e.target.checked);
                      markHasUnsavedChanges(true);
                      setExpandedComments(e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', width: '16px', textAlign: 'center' }}>
                    {expandedComments ? '▲' : '▼'}
                  </span>
                </div>
              </div>
              
              {expandedComments && (
                <div style={{ padding: '0 16px 16px' }}>
                  <RichEditor
                    value={signatureCommentsHtml}
                    onChange={handleSignatureCommentsChange}
                    placeholder='Type your comment signature here'
                    maxLength={1000}
                    toolbar={true}
                    autoFocus={false}
                  />
                  <div className="signature-live-preview" aria-live="polite" style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      className="signature-live-preview-header"
                      onClick={() => setShowSignatureCommentsPreview((prev) => !prev)}
                      aria-expanded={showSignatureCommentsPreview}
                    >
                      <span>Live Preview</span>
                      <span>{showSignatureCommentsPreview ? 'Hide' : 'Show'}</span>
                    </button>
                    {showSignatureCommentsPreview && (
                      <article className="signature-preview-post-card">
                        <header className="signature-preview-post-header">
                          <img
                            src={signaturePreviewAvatarSources[0] || ''}
                            alt={user?.username || 'preview-user'}
                            className="signature-preview-avatar"
                            data-avatar-fallback-index="0"
                            onError={handleSignaturePreviewAvatarError}
                          />
                          <div className="signature-preview-author-block">
                            <span className="signature-preview-author">{user?.username || 'preview-user'}</span>
                            <span className="signature-preview-time">just now</span>
                          </div>
                        </header>

                        <div className="signature-preview-post-body">
                          <p className="signature-preview-context-text">
                            This is how your signature appears appended under your comments.
                          </p><br/>

                          {String(signatureCommentsHtml || '').trim() ? (
                            <>
                              <div className="signature-preview-divider" /><br/>
                              <MarkdownRenderer
                                content={signatureCommentsHtml}
                                isHtml={true}
                                className="signature-live-preview-content"
                                disableCarousel={true}
                              />
                            </>
                          ) : (
                            <p className="signature-live-preview-empty">
                              Your signature preview will appear here as you type.
                            </p>
                          )}
                        </div>
                      </article>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Flux Signature */}
            <div className="av-section" style={{ marginBottom: '24px' }}>
              <div 
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '16px' }}
                onClick={() => setExpandedFlux(!expandedFlux)}
              >
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>Use signature on Flux (articles)</h3>
                  <p className="option-desc" style={{ margin: '4px 0 0', color: 'var(--text-tertiary)' }}>
                    If active, your signature will be appended to your published articles.
                  </p>
                </div>
                <div style={{ paddingLeft: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <input
                    type="checkbox"
                    checked={signatureFluxEnabled}
                    onChange={(e) => {
                      updateSignatureFluxEnabled(e.target.checked);
                      markHasUnsavedChanges(true);
                      setExpandedFlux(e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', width: '16px', textAlign: 'center' }}>
                    {expandedFlux ? '▲' : '▼'}
                  </span>
                </div>
              </div>
              
              {expandedFlux && (
                <div style={{ padding: '0 16px 16px' }}>
                  <RichEditor
                    value={signatureFluxHtml}
                    onChange={handleSignatureFluxChange}
                    placeholder='Type your Flux signature here'
                    maxLength={1000}
                    toolbar={true}
                    autoFocus={false}
                  />
                  <div className="signature-live-preview" aria-live="polite" style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      className="signature-live-preview-header"
                      onClick={() => setShowSignatureFluxPreview((prev) => !prev)}
                      aria-expanded={showSignatureFluxPreview}
                    >
                      <span>Live Preview (Article Style)</span>
                      <span>{showSignatureFluxPreview ? 'Hide' : 'Show'}</span>
                    </button>
                    {showSignatureFluxPreview && (
                      <article className="signature-preview-article" style={{ padding: '30px 20px', background: 'var(--bg-secondary)', borderRadius: '8px', border: 'none', marginTop: '10px' }}>
                        <div className="signature-preview-post-body">
                          <p className="signature-preview-context-text" style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                            ... And this concludes the final paragraph of my awesome article!
                          </p><br/>

                          {String(signatureFluxHtml || '').trim() ? (
                            <>
                              <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '2rem 0' }} />
                              <MarkdownRenderer
                                content={signatureFluxHtml}
                                isHtml={true}
                                className="signature-live-preview-content"
                                disableCarousel={true}
                              />
                            </>
                          ) : (
                            <p className="signature-live-preview-empty">
                              Your signature preview will appear here as you type.
                            </p>
                          )}
                        </div>
                      </article>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Flex Signature */}
            <div className="av-section" style={{ marginBottom: '24px' }}>
              <div 
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '16px' }}
                onClick={() => setExpandedFlex(!expandedFlex)}
              >
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>Use signature on Flex (short posts)</h3>
                  <p className="option-desc" style={{ margin: '4px 0 0', color: 'var(--text-tertiary)' }}>
                    If active, your signature will be appended to top-level short posts.
                  </p>
                </div>
                <div style={{ paddingLeft: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <input
                    type="checkbox"
                    checked={signatureFlexEnabled}
                    onChange={(e) => {
                      updateSignatureFlexEnabled(e.target.checked);
                      markHasUnsavedChanges(true);
                      setExpandedFlex(e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', width: '16px', textAlign: 'center' }}>
                    {expandedFlex ? '▲' : '▼'}
                  </span>
                </div>
              </div>
              
              {expandedFlex && (
                <div style={{ padding: '0 16px 16px' }}>
                  <RichEditor
                    value={signatureFlexHtml}
                    onChange={handleSignatureFlexChange}
                    placeholder='Type your Flex signature here'
                    maxLength={1000}
                    toolbar={true}
                    autoFocus={false}
                  />
                  <div className="signature-live-preview" aria-live="polite" style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      className="signature-live-preview-header"
                      onClick={() => setShowSignatureFlexPreview((prev) => !prev)}
                      aria-expanded={showSignatureFlexPreview}
                    >
                      <span>Live Preview</span>
                      <span>{showSignatureFlexPreview ? 'Hide' : 'Show'}</span>
                    </button>
                    {showSignatureFlexPreview && (
                      <article className="signature-preview-post-card">
                        <header className="signature-preview-post-header">
                          <img
                            src={signaturePreviewAvatarSources[0] || ''}
                            alt={user?.username || 'preview-user'}
                            className="signature-preview-avatar"
                            data-avatar-fallback-index="0"
                            onError={handleSignaturePreviewAvatarError}
                          />
                          <div className="signature-preview-author-block">
                            <span className="signature-preview-author">{user?.username || 'preview-user'}</span>
                            <span className="signature-preview-time">just now</span>
                          </div>
                        </header>

                        <div className="signature-preview-post-body">
                          <p className="signature-preview-context-text">
                            This is how your signature appears appended under your Flex posts.
                          </p><br/>

                          {String(signatureFlexHtml || '').trim() ? (
                            <>
                              <div className="signature-preview-divider" /><br/>
                              <MarkdownRenderer
                                content={signatureFlexHtml}
                                isHtml={true}
                                className="signature-live-preview-content"
                                disableCarousel={true}
                              />
                            </>
                          ) : (
                            <p className="signature-live-preview-empty">
                              Your signature preview will appear here as you type.
                            </p>
                          )}
                        </div>
                      </article>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'muted' ? (
          <div className="muted-section">
            <h2 className="section-title">Muted / Ignored Users</h2>
            <div className="muted-form">
              <input
                type="text"
                placeholder="username"
                value={newMutedUser}
                onChange={e => setNewMutedUser(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddMuted()}
              />
              <button className="btn primary-btn" onClick={handleAddMuted}>Add</button>
            </div>
            <ul className="muted-list">
              {mutedUsers.map(u => (
                <li key={u} className="muted-item">
                  {u}
                  <button
                    className="btn user-unmute-btn"
                    onClick={() => {
                      if (window.confirm(`Unmute @${u}?`)) {
                        removeMutedUser(u);
                      }
                    }}
                    style={{ marginLeft: '8px', fontSize: '0.8rem' }}
                  >Unmute</button>
                </li>
              ))}
            </ul>
          </div>
        ) : activeTab === 'notifications' ? (
          <div className="notifications-settings-section">
            <h2 className="section-title">Notifications Defaults</h2>
            <p className="section-description">Choose default behavior for the notifications page.</p>

            <div className="settings-section">
              <h3 className="section-subtitle">Standard Notification Filters</h3>
              <p className="section-description">Same selection structure as the notifications filter dropdown. Defaults to all selected.</p>

              <div className="notif-settings-filter-actions">
                <button
                  type="button"
                  className="notif-btn notif-btn-secondary"
                  onClick={() => updateNotificationFilterTypes(
                    notificationFilterTypes.length === ALL_NOTIFICATION_TYPES.length
                      ? []
                      : ALL_NOTIFICATION_TYPES
                  )}
                >
                  {notificationFilterTypes.length === ALL_NOTIFICATION_TYPES.length ? 'Deselect all' : 'Select all'}
                </button>
                <span className="notifications-count">
                  Selected: {notificationFilterTypes.length}/{ALL_NOTIFICATION_TYPES.length}
                </span>
              </div>

              <div className="notif-settings-filter-grid">
                {Object.entries(NOTIFICATION_TYPE_LABELS).map(([type, label]) => (
                  <label key={type} className="notif-filter-item">
                    <input
                      type="checkbox"
                      checked={notificationFilterTypes.includes(type)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...notificationFilterTypes, type]
                          : notificationFilterTypes.filter((t) => t !== type);
                        updateNotificationFilterTypes(next);
                      }}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <div className="settings-section">
              <h3 className="section-subtitle">Vote Notifications</h3>
              <label className="scope-option">
                <span className="scope-option-body">
                  <span className="theme-label">Display vote weight</span>
                  <span className="scope-note">Default is off. When enabled, vote/downvote notifications show percentage weight.</span>
                </span>
                <input
                  type="checkbox"
                  checked={notificationDisplayVoteWeight}
                  onChange={(e) => updateNotificationDisplayVoteWeight(e.target.checked)}
                />
              </label>
            </div>

            <div className="settings-section notifications-beta">
              <h3 className="section-subtitle">Notiflexions (Beta)</h3>
              <label className="scope-option">
                <span className="scope-option-body">
                  <span className="theme-label">Enable Notiflexions</span>
                  <span className="scope-note">Experimental beta feature for grouped and list-based notifications.</span>
                </span>
                <input
                  type="checkbox"
                  checked={notiflexionsEnabled}
                  onChange={(e) => updateNotiflexionsEnabled(e.target.checked)}
                />
              </label>

              {notiflexionsEnabled && (
                <div className="notifications-beta-advanced">
                  <label className="scope-option">
                    <span className="scope-option-body">
                      <span className="theme-label">Group by post/action</span>
                      <span className="scope-note">Groups repeated actions on the same post into one parent notification with sub-notifications.</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={notiflexionsGroupByPostAction}
                      onChange={(e) => updateNotiflexionsGroupByPostAction(e.target.checked)}
                    />
                  </label>

                  <label className="scope-option">
                    <span className="scope-option-body">
                      <span className="theme-label">Show Notiflexions from lists</span>
                      <span className="scope-note">Creates notifications when selected list members publish long-form articles.</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={notiflexionsFromListsEnabled}
                      onChange={(e) => updateNotiflexionsFromListsEnabled(e.target.checked)}
                    />
                  </label>

                  {notiflexionsFromListsEnabled && (
                    <div className="notiflexions-list-picker">
                      {(lists || []).length === 0 ? (
                        <p className="scope-note">No lists found yet.</p>
                      ) : (
                        (lists || []).map((list) => {
                          const isSelected = effectiveNotiflexionsListIds.includes(list.id);
                          const commentsIncluded = (notiflexionsListIncludeComments || []).includes(list.id);

                          return (
                            <div key={list.id} className="notiflexions-list-item-group">
                              <label className="notif-filter-item">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  disabled={Boolean(list?.is_favourite)}
                                  onChange={(e) => {
                                    if (list?.is_favourite) return;
                                    const next = e.target.checked
                                      ? [...notiflexionsListIds, list.id]
                                      : notiflexionsListIds.filter((id) => id !== list.id);
                                    updateNotiflexionsListIds(next);
                                  }}
                                />
                                {list.name}{list?.is_favourite ? ' (always on)' : ''}
                              </label>

                              {isSelected && (
                                <div className="notiflexions-list-suboptions">
                                  <label className="notiflexions-list-suboption-toggle">
                                    <input
                                      type="checkbox"
                                      checked={(notiflexionsListIncludeShorts || []).includes(list.id)}
                                      onChange={(e) => {
                                        const next = e.target.checked
                                          ? [...(notiflexionsListIncludeShorts || []), list.id]
                                          : (notiflexionsListIncludeShorts || []).filter((id) => id !== list.id);
                                        updateNotiflexionsListIncludeShorts(next);
                                      }}
                                    />
                                    <span>Include short content</span>
                                  </label>

                                  <label className="notiflexions-list-suboption-toggle">
                                    <input
                                      type="checkbox"
                                      checked={commentsIncluded}
                                      onChange={(e) => {
                                        const next = e.target.checked
                                          ? [...(notiflexionsListIncludeComments || []), list.id]
                                          : (notiflexionsListIncludeComments || []).filter((id) => id !== list.id);
                                        updateNotiflexionsListIncludeComments(next);
                                      }}
                                    />
                                    <span>Include comments</span>
                                  </label>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}

                  <label className="scope-option">
                    <span className="scope-option-body">
                      <span className="theme-label">Control muted behavior</span>
                      <span className="scope-note">When enabled, muted authors can be displayed normally, displayed muted, or ignored completely.</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={notiflexionsMutedBehaviorEnabled}
                      onChange={(e) => updateNotiflexionsMutedBehaviorEnabled(e.target.checked)}
                    />
                  </label>

                  {notiflexionsMutedBehaviorEnabled && (
                    <div className="start-feed-sub">
                      <select
                        className="community-sort-dropdown"
                        value={notiflexionsMutedBehaviorMode}
                        onChange={(e) => updateNotiflexionsMutedBehaviorMode(e.target.value)}
                      >
                        <option value="display_normal">display normal</option>
                        <option value="display_muted">display muted</option>
                        <option value="ignore_completely">ignore completely</option>
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'profile' ? (
          <div className="profile-section">
            <h2 className="section-title">Profile Info</h2>
            <p className="section-description">Update your public profile.</p>
            {profileLoading ? (
              <p style={{ color: 'var(--text-secondary)' }}>Loading profile...</p>
            ) : (
              <div className="profile-settings-form">
                <div className="profile-field">
                  <label htmlFor="profile-name">Display Name</label>
                  <input
                    id="profile-name"
                    type="text"
                    placeholder="Your display name"
                    value={profileForm.name}
                    onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))}
                    maxLength={50}
                  />
                </div>
                <div className="profile-field">
                  <label htmlFor="profile-about">About / Bio</label>
                  <RichEditor
                    value={profileAboutHtml}
                    onChange={html => {
                      setProfileAboutHtml(html);
                      setProfileForm(f => ({ ...f, about: htmlToMarkdown(html) }));
                    }}
                    placeholder="A short bio about yourself"
                    maxLength={160}
                    toolbar={true}
                    autoFocus={false}
                  />
                </div>
                <div className="profile-field">
                  <label htmlFor="profile-image">Profile Image URL</label>
                  <input
                    id="profile-image"
                    type="url"
                    placeholder="https://..."
                    value={profileForm.profileImage}
                    onChange={e => setProfileForm(f => ({ ...f, profileImage: e.target.value }))}
                  />
                  {profileForm.profileImage && (
                    <div className="settings-image-preview-grid">
                      <button
                        type="button"
                        className="settings-image-thumb-btn"
                        onClick={() => setLightboxImage(profileForm.profileImage)}
                        aria-label="Preview profile image"
                      >
                        <img src={profileForm.profileImage} alt="Profile preview" loading="lazy" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="profile-field">
                  <label htmlFor="cover-image">Cover Image URL</label>
                  <input
                    id="cover-image"
                    type="url"
                    placeholder="https://..."
                    value={profileForm.coverImage}
                    onChange={e => setProfileForm(f => ({ ...f, coverImage: e.target.value }))}
                  />
                  {profileForm.coverImage && (
                    <div className="settings-image-preview-grid">
                      <button
                        type="button"
                        className="settings-image-thumb-btn settings-image-thumb-btn--wide"
                        onClick={() => setLightboxImage(profileForm.coverImage)}
                        aria-label="Preview cover image"
                      >
                        <img src={profileForm.coverImage} alt="Cover preview" loading="lazy" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="profile-field">
                  <label htmlFor="profile-website">Website</label>
                  <input
                    id="profile-website"
                    type="url"
                    placeholder="https://yoursite.com"
                    value={profileForm.website}
                    onChange={e => setProfileForm(f => ({ ...f, website: e.target.value }))}
                  />
                </div>
                <div className="profile-field">
                  <label htmlFor="profile-location">Location</label>
                  <input
                    id="profile-location"
                    type="text"
                    placeholder="City, Country"
                    value={profileForm.location}
                    onChange={e => setProfileForm(f => ({ ...f, location: e.target.value }))}
                    maxLength={60}
                  />
                </div>

                <div className="profile-field">
                  <label>Default post beneficiaries</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button
                      type="button"
                      className="secondary-btn blwh-btn"
                      onClick={() => setShowDefaultBeneficiariesModal(true)}
                    >
                      Edit beneficiaries
                    </button>
                    {defaultBeneficiaries.length > 0 && (
                      <span className="benef-summary">
                        {defaultBeneficiaries.map(t => {
                          const [a, w] = t.split('=');
                          return `${a}=${w}%`;
                        }).join(', ')}
                      </span>
                    )}
                  </div>
                  <BeneficiariesModal
                    isOpen={showDefaultBeneficiariesModal}
                    onClose={() => setShowDefaultBeneficiariesModal(false)}
                    initialTags={defaultBeneficiaries}
                    onSave={updateDefaultBeneficiaries}
                  />
                </div>

                <div className="profile-advanced-section">
                  <button
                    type="button"
                    className="profile-advanced-toggle"
                    onClick={() => setShowAdvancedOptions(v => !v)}
                    aria-expanded={showAdvancedOptions}
                  >
                    <span>Advanced options</span>
                    <span className={`profile-advanced-chevron${showAdvancedOptions ? ' open' : ''}`}>▾</span>
                  </button>
                  {showAdvancedOptions && (
                <div className="profile-scope-section">
                  <p className="profile-scope-title">Update scope</p>
                  <div className="scope-options">
                    <div className="scope-option locked">
                      <div className="scope-option-body">
                        <label>
                          <input type="checkbox" checked readOnly />
                          Update Flexus
                        </label>
                        <span className="scope-note">Always enabled - saves to <code>posting_json_metadata.proflex</code>.</span>
                      </div>
                    </div>
                    <div className="scope-option">
                      <div className="scope-option-body">
                        <label>
                          <input
                            type="checkbox"
                            checked={updateMostHive}
                            onChange={e => setUpdateMostHive(e.target.checked)}
                          />
                          Update most of Hive
                        </label>
                        <span className="scope-note">Also writes <code>posting_json_metadata.profile</code> and flat fields (PeakD, Ecency, etc.).</span>
                      </div>
                      <button
                        type="button"
                        className="scope-info-btn"
                        onClick={() => setShowScopeInfoModal(true)}
                        aria-label="Compatibility info"
                        style={{ visibility: updateMostHive ? 'visible' : 'hidden' }}
                      >ℹ</button>
                    </div>
                    <div className={`scope-option${!canUpdateAllHive ? ' unavailable' : ''}`}>
                      <div className="scope-option-body">
                        <label>
                          <input
                            type="checkbox"
                            checked={updateAllHive}
                            disabled={!canUpdateAllHive}
                            onChange={e => setUpdateAllHive(e.target.checked)}
                          />
                          Update all Hive
                        </label>
                        <span className="scope-note">
                          {canUpdateAllHive
                            ? 'Also updates json_metadata (Keychain active key).'
                            : 'Requires Hive Keychain login - active key needed, not available with posting key.'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                  )}
                </div>

                <div className="profile-save-row">
                  <button
                    className="profile-save-btn"
                    onClick={handleProfileSave}
                    disabled={profileSaving}
                  >
                    {profileSaving ? 'Saving...' : 'Save Profile'}
                  </button>
                  {profileSaveStatus && (
                    <span className={`profile-save-status ${profileSaveStatus.type}`}>
                      {profileSaveStatus.type === 'success' ? '✓ ' : '✗ '}
                      {profileSaveStatus.message}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Start Feed Selection */}
            <div className="settings-section">
              <h2 className="section-title">Start Feed</h2>
              <p className="section-description">Choose what you see when you open the app</p>
              
              <div className="layout-grid" style={{ marginBottom: 16 }}>
                <button
                  className={`layout-card ${startFeed === 'flex' ? 'active' : ''}`}
                  onClick={() => updateStartFeed('flex')}
                >
                  <div className="theme-content">
                    <div className="theme-text">
                      <span className="theme-label">Flex</span>
                      <p className="theme-description">Short-form feeds</p>
                    </div>
                  </div>
                  {startFeed === 'flex' && <span className="active-indicator">✓</span>}
                </button>
                <button
                  className={`layout-card ${startFeed === 'flux' ? 'active' : ''}`}
                  onClick={() => updateStartFeed('flux')}
                >
                  <div className="theme-content">
                    <div className="theme-text">
                      <span className="theme-label">Flux</span>
                      <p className="theme-description">Articles feed</p>
                    </div>
                  </div>
                  {startFeed === 'flux' && <span className="active-indicator">✓</span>}
                </button>
              </div>

              {startFeed === 'flex' && (
                <div className="start-feed-sub">
                  <p className="section-description">Default Flex feed</p>
                  <select
                    className="community-sort-dropdown"
                    value={startFeedPath}
                    onChange={e => updateStartFeedPath(e.target.value)}
                  >
                    {settingsEnabledFeeds.map(f => (
                      <option key={f.key} value={`/feed/${f.key}`}>{f.label}</option>
                    ))}
                    {listsWithMembers.length > 0 && (
                      <optgroup label="Lists">
                        {listsWithMembers.map(list => (
                          <option key={list.id} value={`/feed/${encodeURIComponent(list.name)}`}>
                            {list.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
              )}

              {startFeed === 'flux' && (() => {
                const fluxMatch = startFeedPath.match(/^\/articles\/([^/]+)\/([^/]+)$/);
                const fluxApp = fluxMatch ? fluxMatch[1] : 'all';
                const fluxSort = fluxMatch ? fluxMatch[2] : 'created';
                return (
                  <div className="start-feed-sub flux-sort">
                    <p className="section-description">Default sort</p>
                    <select
                      className="community-sort-dropdown"
                      value={fluxSort}
                      onChange={e => updateStartFeedPath(`/articles/${fluxApp}/${e.target.value}`)}
                    >
                      <option value="created">Recent</option>
                      <option value="trending">Trending</option>
                      {user && <option value="following">Following</option>}
                    </select>

                    <p className="section-description">Default app</p>
                    <select
                      className="community-sort-dropdown"
                      value={fluxApp}
                      onChange={e => updateStartFeedPath(`/articles/${e.target.value}/${fluxSort}`)}
                    >
                      {APP_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                );
              })()}
            </div>

            {/* Theme Selection */}
            <div className="settings-section">
              <h2 className="section-title">Base Theme</h2>
              <p className="section-description">Choose your preferred theme style</p>
              
              <div className="theme-grid">
                {themes.map((t) => (
                  <button
                    key={t.value}
                    className={`theme-card ${theme === t.value ? 'active' : ''}`}
                    onClick={() => updateTheme(t.value)}
                  >
                    <div className="theme-content">
                      <div className="theme-text">
                        <span className="theme-label">{t.label}</span>
                        <p className="theme-description">{t.description}</p>
                      </div>
                      <div className="color-preview" style={{ backgroundColor: t.bgColor }}>
                        {theme === t.value && (
                          <span className="checkmark">✓</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Accent Color Selection */}
            <div className="settings-section">
              <h2 className="section-title">Accent Color</h2>
              <p className="section-description">Pick your favorite accent color</p>
              
              <div className="accent-grid">
                {accentColors.map((accent) => (
                  <button
                    key={accent.value}
                    className={`accent-card ${accentColor === accent.value ? 'active' : ''}`}
                    onClick={() => updateAccentColor(accent.value)}
                  >
                    <div
                      className="color-preview"
                      style={{ backgroundColor: accent.color }}
                    />
                    <span className="accent-label">{accent.label}</span>
                    {accentColor === accent.value && (
                      <span className="active-indicator">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="settings-section">
              <h2 className="section-title">Layout</h2>
              <h4 className="section-subtitle">Choose your preferred layout settings</h4>
              
              {/* Layout Mode Selection */}
              <p className="section-description">Navigation Layout</p>
              <div className="layout-grid">
                <button
                  className={`layout-card ${layoutMode === 'sidebar' ? 'active' : ''}`}
                  onClick={() => updateLayoutMode('sidebar')}
                >
                  <div className="layout-preview sidebar-preview">
                    <div className="preview-sidebar"></div>
                    <div className="preview-content"></div>
                    <div className="preview-sidebar right"></div>
                  </div>
                  <span className="layout-label">Sidebar Layout</span>
                  {layoutMode === 'sidebar' && (
                    <span className="active-indicator">✓</span>
                  )}
                </button>

                <button
                  className={`disabled layout-card ${layoutMode === 'header' ? 'active' : ''}`}
                  onClick={() => updateLayoutMode('header')}
                  disabled
                >
                  <div className="layout-preview header-preview">
                    <div className="preview-header"></div>
                    <div className="preview-content"></div>
                  </div>
                  <span className="layout-label">Header Layout</span>
                  <span className="coming-soon">Coming Soon</span>
                  {layoutMode === 'header' && (
                    <span className="active-indicator">✓</span>
                  )}
                </button>
              </div>

              {/* Main content mode selection */}
              <p className="section-description">Maincontent Layout</p>
              <div className="layout-grid main-content-mode">
                <button
                  className={`layout-card ${maincontentMode === 'normal' ? 'active' : ''}`}
                  onClick={() => updateMaincontentMode('normal')}
                >
                  <div className="mc-preview normalmode-preview">
                    {layoutMode === 'sidebar' ? (
                      <div className="layout-preview sidebar-preview">
                        <div className="preview-sidebar"></div>
                        <div className="preview-content" style={{ maxWidth: "40%", margin: "0 auto" }}>
                          <div className="flux-row" style={{height: "30%", background: "var(--input-border)", borderRadius: 0}}></div>
                          <div className="flux-row" style={{height: "20%", background: "var(--input-border)", margin: "2%"}}></div>
                          <div className="flux-row" style={{height: "20%", background: "var(--input-border)", margin: "2%"}}></div>
                          <div className="flux-row" style={{height: "20%", background: "var(--input-border)", margin: "2%"}}></div>
                        </div>
                        <div className="preview-sidebar right"></div>
                      </div>
                    ) : (
                      <div className="layout-preview header-preview">
                        <div className="preview-header"></div>
                        <div className="preview-content" style={{maxWidth: "45%", transform: "translateX(60%)"}}></div>
                      </div>
                    )}
                  </div>
                  <span className="layout-label">Normal layout</span>
                  {maincontentMode === 'normal' && (
                    <span className="active-indicator">✓</span>
                  )}
                </button>

                <button
                  className={`layout-card ${maincontentMode === 'wide' ? 'active' : ''}`}
                  onClick={() => updateMaincontentMode('wide')}
                >
                  <div className="mc-preview wide-preview">
                    {layoutMode === 'sidebar' ? (
                      <div className="layout-preview sidebar-preview">
                        <div className="preview-sidebar"></div>
                        <div className="preview-content" style={{ margin: "0 auto" }}>
                          <div className="flux-row" style={{height: "30%", background: "var(--input-border)", borderRadius: 0}}></div>
                          <div className="flux-row" style={{height: "20%", background: "var(--input-border)", margin: "2%"}}></div>
                          <div className="flux-row" style={{height: "20%", background: "var(--input-border)", margin: "2%"}}></div>
                          <div className="flux-row" style={{height: "20%", background: "var(--input-border)", margin: "2%"}}></div>
                        </div>
                        <div className="preview-sidebar right"></div>
                      </div>
                    ) : (
                      <div className="layout-preview header-preview">
                        <div className="preview-header"></div>
                        <div className="preview-content" style={{maxWidth: "60%", transform: "translateX(33%)"}}></div>
                      </div>
                    )}
                  </div>
                  <span className="layout-label">Wide layout</span>
                  {maincontentMode === 'wide' && (
                    <span className="active-indicator">✓</span>
                  )}
                </button>
              </div>

              {/* Comment Display (preview + selection) */}
              <p className="section-description">Comments display</p>

              <div className="layout-grid">
                <button
                  className={`layout-card ${commentDisplayMode === 'card' ? 'active' : ''}`}
                  onClick={() => { updateCommentDisplayMode('card'); }}
                >
                  <div className="comment-preview comment-preview-card">
                    <div className="preview-card preview-post">
                      <div className="preview-card-header">
                        <span className="preview-avatar"></span>
                        <span className="preview-line short"></span>
                        <span className="preview-line tiny"></span>
                      </div>
                      <div className="preview-card-body">
                        <span className="preview-line"></span>
                        <span className="preview-line short"></span>
                      </div>
                      <div className="preview-card-actions">
                        <span></span><span></span><span></span>
                      </div>
                    </div>
                    <div className="preview-thread card-thread">
                      <div className="preview-card preview-comment level-1">
                        <div className="preview-card-header">
                          <span className="preview-avatar small"></span>
                          <span className="preview-line short"></span>
                        </div>
                        <div className="preview-card-body">
                          <span className="preview-line"></span>
                          <span className="preview-line short"></span>
                        </div>
                      </div>
                      <div className="preview-card preview-comment level-2">
                        <div className="preview-card-header">
                          <span className="preview-avatar small"></span>
                          <span className="preview-line short"></span>
                        </div>
                        <div className="preview-card-body">
                          <span className="preview-line short"></span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <span className="layout-label">Card</span>
                  {commentDisplayMode === 'card' && (<span className="active-indicator">✓</span>)}
                </button>

                <button
                  className={`layout-card ${commentDisplayMode === 'inline' ? 'active' : ''}`}
                  onClick={() => { updateCommentDisplayMode('inline'); }}
                >
                  <div className="comment-preview comment-preview-inline">
                    <div className="preview-card preview-post">
                      <div className="preview-card-header">
                        <span className="preview-avatar"></span>
                        <span className="preview-line short"></span>
                        <span className="preview-line tiny"></span>
                      </div>
                      <div className="preview-card-body">
                        <span className="preview-line"></span>
                        <span className="preview-line short"></span>
                      </div>
                    </div>
                    <div className="preview-inline-thread">
                      <div className="preview-inline-row">
                        <span className="preview-dot"></span>
                        <span className="preview-line"></span>
                        <span className="preview-pill"></span>
                      </div>
                      <div className="preview-inline-row nested">
                        <span className="preview-dot"></span>
                        <span className="preview-line short"></span>
                      </div>
                    </div>
                  </div>
                  <span className="layout-label">Inline</span>
                  {commentDisplayMode === 'inline' && (<span className="active-indicator">✓</span>)}
                </button>
              </div>

              {/* Communities View Mode */}
              <p className="section-description">Flux view (long form posts)</p>
              <div className="flux-grid layout-grid">
                <button
                  className={`layout-card ${communitiesViewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => {
                    updateCommunitiesViewMode('grid');
                  }}
                >
                  <div className="flux-preview grid-preview">
                    <div className="flux-tile"></div>
                    <div className="flux-tile"></div>
                    <div className="flux-tile"></div>
                    <div className="flux-tile"></div>
                    <div className="flux-tile"></div>
                    <div className="flux-tile"></div>
                  </div>
                  <span className="layout-label">Grid</span>
                  {communitiesViewMode === 'grid' && (
                    <span className="active-indicator">✓</span>
                  )}
                </button>

                <button
                  className={`layout-card ${communitiesViewMode === 'list' ? 'active' : ''}`}
                  onClick={() => {
                    updateCommunitiesViewMode('list');
                  }}
                >
                  <div className="flux-preview list-preview">
                    <div className="flux-row"></div>
                    <div className="flux-row"></div>
                    <div className="flux-row"></div>
                  </div>
                  <span className="layout-label">List</span>
                  {communitiesViewMode === 'list' && (
                    <span className="active-indicator">✓</span>
                  )}
                </button>
              </div>

              <p className="section-description">Image Display Mode</p>
              <div className="layout-grid">
                <button
                  className={`layout-card ${imageDisplayMode === 'inline' ? 'active' : ''}`}
                  onClick={() => { updateImageDisplayMode('inline'); }}
                >
                  <div className="comment-preview comment-preview-inline">
                    <div className="preview-card preview-post">
                      <div className="preview-card-body">
                        <span className="preview-line short"></span>
                        <span className="preview-line"></span>
                        <span className="preview-line short"></span>
                      </div>
                    </div>
                  </div>
                  <span className="layout-label">Inline in post</span>
                  {imageDisplayMode === 'inline' && (<span className="active-indicator">✓</span>)}
                </button>

                <button
                  className={`layout-card ${imageDisplayMode === 'carousel' ? 'active' : ''}`}
                  onClick={() => { updateImageDisplayMode('carousel'); }}
                >
                  <div className="comment-preview comment-preview-card">
                    <div className="preview-card preview-post">
                      <div className="preview-card-body">
                        <span className="preview-line short"></span>
                        <span className="preview-line"></span>
                        <span className="preview-line tiny"></span>
                        <span className="preview-line short"></span>
                      </div>
                    </div>
                  </div>
                  <span className="layout-label">Carousel after content</span>
                  {imageDisplayMode === 'carousel' && (<span className="active-indicator">✓</span>)}
                </button>
              </div>

              {/* Feed Behavior Setting */}
              <p className="section-description" style={{ marginTop: 24 }}>Feed Behavior</p>
              <div className="voting-controls" style={{ marginTop: 14 }}>
                <label className="toggle-row">
                  <input
                    type="checkbox"
                    checked={autoload_more}
                    onChange={(e) => { updateAutoloadMore(e.target.checked); markHasUnsavedChanges(true); }}
                  />
                  <span style={{ marginLeft: 8 }}>Auto-load more posts as you scroll</span>
                </label>
                <p className="option-desc" style={{ marginLeft: 20, marginBottom: 12 }}>
                  If enabled, feeds will automatically fetch the next page of posts when you reach the bottom.
                </p>
              </div>

            </div>

            {/* Voting Settings */}
            <div className="settings-section">
              <h2 className="section-title">Voting</h2>
              <p className="section-description">Control your default vote weight and whether the slider is shown when voting</p>

              <div className="voting-controls">
                <label className="toggle-row">
                  <input
                    type="checkbox"
                    checked={showVoteSlider}
                    onChange={(e) => { updateShowVoteSlider(e.target.checked); markHasUnsavedChanges(true); }}
                  />
                  <span style={{ marginLeft: 8 }}>Show vote slider when voting</span>
                </label>
                <p className="option-desc" style={{ marginLeft: 20 }}>
                  If the slider is hidden, votes will use your saved default weight.
                </p>

                <div style={{ marginTop: 12 }}>
                  <label style={{ display: 'block', marginBottom: 6 }}>Default vote weight: {voteDefaultWeight}%</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={voteDefaultWeight}
                    onChange={(e) => { updateVoteDefaultWeight(parseInt(e.target.value, 10)); markHasUnsavedChanges(true); }}
                  />
                </div>

                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--input-border)' }}>
                  <label style={{ display: 'block', marginBottom: 10 }}>Reward value display</label>
                  <select
                    value={rewardDisplayMode}
                    onChange={(e) => { updateRewardDisplayMode(e.target.value); markHasUnsavedChanges(true); }}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '4px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text-primary)', cursor: 'pointer' }}
                  >
                    <option value="default">Default (Author + Curator rewards)</option>
                    <option value="author_only">Author Only</option>
                  </select>
                  <p className="option-desc">
                    Default shows vote rewards for both author and curators. Author Only shows only the author's share.
                  </p>
                  <div className="reward-currency-setting">
                    <span className="reward-currency-label">Reward currency</span>
                    <button
                      type="button"
                      className={`currency-toggle ${rewardDisplayCurrency === 'hbd' ? 'right' : 'left'}`}
                      onClick={() => {
                        const nextCurrency = rewardDisplayCurrency === 'hive' ? 'hbd' : 'hive';
                        updateRewardDisplayCurrency(nextCurrency);
                        markHasUnsavedChanges(true);
                      }}
                      aria-label="Reward currency"
                    >
                      <span className={`currency-option ${rewardDisplayCurrency === 'hive' ? 'active' : ''}`}>HIVE</span>
                      <span className={`currency-option ${rewardDisplayCurrency === 'hbd' ? 'active' : ''}`}>HBD</span>
                      <span className="currency-slider" />
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--input-border)' }}>
                  <label className="toggle-row">
                    <input
                      type="checkbox"
                      checked={singleNotifyOnBlockchain}
                      onChange={(e) => {
                        updateSingleNotifyOnBlockchain(e.target.checked);
                        markHasUnsavedChanges(true);
                      }}
                    />
                    <span style={{ marginLeft: 8 }}>Mark single notifications on blockchain</span>
                  </label>
                  <p className="option-desc" style={{ marginLeft: 20 }}>
                    When enabled, marking one notification as read sends a <code>single_notify</code> custom_json with the related permlink.
                  </p>
                </div>
              </div>
            </div>

            {/* Preview Info */}
            <div className="settings-section">
              <div className="preview-info">
                <span className="preview-icon">👁️</span>
                <div className="preview-text">
                  <strong>Live Preview</strong>
                  <p>Changes are previewed in real-time but not saved until you click Save Changes</p>
                </div>
              </div>
            </div>

            {/* Data Sync Section */}
            <div className="settings-section">
              <h2 className="section-title">Data Sync</h2>
              <p className="section-description">Sync your followers and notifications from Hive blockchain</p>
              
              <div className="sync-info">
                <p>Click the button below to:</p>
                <ul>
                  <li>Update your follower list from Hive</li>
                  <li>Sync notifications since your last activity</li>
                  <li>Ensure your profile is up to date</li>
                </ul>
              </div>

              <button
                className="sync-btn"
                onClick={handleSyncData}
                /*disabled={syncing}*/
                disabled={true}
              >
                {syncing ? (
                  <>
                    <span className="sync-spinner">⟳</span>
                    Syncing Data...
                  </>
                ) : (
                  <>
                    <span className="sync-icon">🔄</span>
                    Sync Data from Hive
                  </>
                )}
              </button> <span style={{ fontSize: '12px', fontStyle: 'italic' }}>Coming soon</span>

              {syncStatus && (
                <div className={`sync-status ${syncStatus.type}`}>
                  {syncStatus.type === 'success' && '✓ '}
                  {syncStatus.type === 'warning' && '⚠ '}
                  {syncStatus.type === 'error' && '✗ '}
                  {syncStatus.message}
                </div>
              )}

              {(hiveService.getLoginMethod() === 'keychain' || hiveService.getLoginMethod() === 'postingkey') && (
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-color)' }}>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0 0 6px' }}>Stored posting key</h3>
                  <p style={{ fontSize: '0.83rem', color: 'var(--text-secondary)', margin: '0 0 12px' }}>
                    Used by the autovote and the post scheduler. Encrypted - never accessible in plaintext.
                  </p>

                  {hasStoredPostingKey ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: '0.83rem', color: 'var(--text-secondary)', margin: '0 0 10px' }}>
                          ✓ A posting key is currently stored.
                        </p>
                        <button
                          className="sync-btn"
                          style={{ backgroundColor: '#fd7e14', color: '#fff', borderColor: '#fd7e14' }}
                          onClick={handleClearStoredPostingKey}
                          disabled={clearingKey}
                        >
                          {clearingKey ? 'Removing…' : 'Clear stored posting key'}
                        </button>
                      </div>
                      <Link to="/autovoting" style={{ fontSize: '0.9rem', color: '#0d6efd', textDecoration: 'none', fontWeight: 500 }}>
                        Configure Autovoting →
                      </Link>
                    </div>
                  ) : (
                    <>
                      <p style={{ fontSize: '0.83rem', color: 'var(--text-secondary)', margin: '0 0 10px' }}>
                        No posting key is currently stored.
                      </p>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                        <input
                          type="password"
                          className="title-input"
                          placeholder="5K… posting key"
                          value={newPostingKey}
                          onChange={e => { setNewPostingKey(e.target.value); setNewPostingKeyStatus('idle'); }}
                          style={{ flex: 1 }}
                          autoComplete="off"
                        />
                        <button
                          type="button"
                          className="sync-btn"
                          onClick={handleVerifyNewPostingKey}
                          disabled={newPostingKeyStatus === 'verifying' || !newPostingKey.trim()}
                          style={{ padding: '10px 18px', fontSize: '0.9rem', whiteSpace: 'nowrap' }}
                        >
                          {newPostingKeyStatus === 'verifying' ? 'Checking…' : 'Verify'}
                        </button>
                      </div>
                      {newPostingKeyStatus === 'valid'   && <div className="cover-status cover-status-valid"   style={{ marginBottom: 8 }}>✓ Key verified</div>}
                      {newPostingKeyStatus === 'invalid' && <div className="cover-status cover-status-invalid" style={{ marginBottom: 8 }}>✗ Invalid posting key</div>}
                      <button
                        className="sync-btn"
                        onClick={handleSaveNewPostingKey}
                        disabled={newPostingKeyStatus !== 'valid' || savingKey}
                      >
                        {savingKey ? 'Saving…' : 'Save posting key'}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Current Selection Display */}
            <div className="current-selection">
              <div className="selection-item">
                <span className="selection-label">Current Theme:</span>
                <span className="selection-value">{theme.charAt(0).toUpperCase() + theme.slice(1)}</span>
              </div>
              <div className="selection-item">
                <span className="selection-label">Accent Color:</span>
                <span className="selection-value">{accentColor.charAt(0).toUpperCase() + accentColor.slice(1)}</span>
              </div>
              <div className="selection-item">
                <span className="selection-label">Layout Mode:</span>
                <span className="selection-value">{layoutMode === 'sidebar' ? 'Sidebar' : 'Header'}</span>
              </div>
              <div className="selection-item">
                <span className="selection-label">Maincontent Mode:</span>
                <span className="selection-value">{maincontentMode === 'normal' ? 'Normal' : 'Wide'}</span>
              </div>
              <div className="selection-item">
                <span className="selection-label">Notification Mode:</span>
                <span className="selection-value">Regular Hive Notifications (managed by server)</span>
              </div>
              <div className="selection-item">
                <span className="selection-label">Single Mark On-chain:</span>
                <span className="selection-value">{singleNotifyOnBlockchain ? 'Enabled' : 'Disabled'}</span>
              </div>
              <div className="selection-item">
                <span className="selection-label">Image Display:</span>
                <span className="selection-value">{imageDisplayMode === 'inline' ? 'Inline in post' : 'Carousel after content'}</span>
              </div>
            </div>
          </>
        )} {/* end of settings tab content */}

      </div>

      {/* Save Button at Bottom */}
      {(activeTab === 'settings' || activeTab === 'notifications' || activeTab === 'signature') && (
      <div className="settings-footer">
        {hasUnsavedChanges && (
          <div className="unsaved-indicator">
            <span className="unsaved-dot">●</span>
            <span>Unsaved changes</span>
          </div>
        )}
        
        <div className="footer-buttons">
          {hasUnsavedChanges && (
            <button className="reset-btn" onClick={handleReset}>
              Discard Changes
            </button>
          )}
          <button
            className={`save-btn ${hasUnsavedChanges ? 'has-changes' : ''}`}
            onClick={handleSave}
            disabled={!hasUnsavedChanges || savingAll}
          >
            {savingAll ? 'Saving...' : (hasUnsavedChanges ? 'Save Changes' : 'No Changes to Save')}
          </button>
        </div>
      </div>
      )}
      </div>

      {showScopeInfoModal && createPortal(
        <div
          className="settings-modal-backdrop"
          onClick={e => e.target === e.currentTarget && setShowScopeInfoModal(false)}
        >
          <div className="settings-info-modal" role="dialog" aria-modal="true" aria-labelledby="scope-info-title">
            <div className="settings-info-modal-header">
              <h3 id="scope-info-title">Compatibility note</h3>
              <button
                type="button"
                className="settings-info-modal-close"
                onClick={() => setShowScopeInfoModal(false)}
                aria-label="Close"
              >×</button>
            </div>
            <div className="settings-info-modal-body">
              <p>
                May not look correct on other platforms if you have used markdown features
                (e.g. shortcode tags) in your about section, as those are only supported on Flexus.
              </p>
              <p style={{ marginTop: '21px' }}>
                <b>
                  If in doubt, only update your profile on Flexus.<br/>
                  <i>OR</i><br/>
                  don't use any markdown in your about section - it will look the same everywhere.
                </b>
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}
      {lightboxImage && (
        <Lightbox
          images={[lightboxImage]}
          currentIndex={0}
          onClose={() => setLightboxImage(null)}
        />
      )}
    </>
  );
};

export default Settings;